import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import $ from "jquery";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import { EventsTimeRangePicker } from "../../../common/Greens";
import { StyledMenu } from "../../application/analytics/styled";
import { PeriodItemComponent } from "../../application/analytics/common";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import SiteScopeGate from "../../../common/SiteScopeGate";
import PermissionGate from "../../../guards/PermissionGate";
import { formatDate } from "../../../../utils/format";
import { tableHeadSx, tableBodySx } from "./WAFEvent";
import { getDefaultEventListingCustomDateRange } from "./eventTimeRange";

import useEvent from "../../../../hooks/user/useEvent";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import useRateLimit from "../../../../hooks/user/useRateLimit";
import useHome from "../../../../hooks/user/useHome";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import EventFilterBar from "./EventFilterBar";
import EventDetailDrawer from "./EventDetailDrawer";
import { buildConditionsFromFilters, withSearchCondition } from "./eventFilterConditions";
import { WafAction } from "../../../../utils/constants";

const SEARCH_DEBOUNCE_MS = 500;

function getRateLimitActionFilterLabel(event) {
  const action = String(event?.action || "").trim().toLowerCase();
  const blocked = event?.blocked_bool || event?.sd_rate_limited === 1;
  if (action === "block" || blocked) return "Blocked";
  if (action === "challenge") return "Challenge";
  if (action === "log_only") return "Log only";
  return null;
}

// Small inline pills for the v2 rate-limit list. Kept in this file
// because they're list-specific and not used elsewhere.
function RlActionPill({ action, blocked }) {
  const a = String(action || "").toLowerCase();
  if (!a && !blocked) {
    return <Typography sx={tableBodySx}>—</Typography>;
  }
  const cfg = a === "block" || blocked
    ? { bg: "#FEE2E2", fg: "#B91C1C", label: a === "block" ? "Block" : "Blocked" }
    : a === "challenge"
    ? { bg: "#FFEDD5", fg: "#9A3412", label: "Challenge" }
    : a === "log_only"
    ? { bg: colors.neutral[100], fg: colors.neutral[700], label: "Log only" }
    : { bg: colors.neutral[100], fg: colors.neutral[700], label: a || "—" };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: 1.25, height: 22, borderRadius: 999,
      backgroundColor: cfg.bg, color: cfg.fg,
      fontSize: 11.5, fontWeight: 600,
    }}>{cfg.label}</Box>
  );
}

function RlModePill({ mode }) {
  const m = String(mode || "").toLowerCase();
  if (!m) {
    return <Typography sx={tableBodySx}>—</Typography>;
  }
  const cfg = m === "enforce"
    ? { bg: "#E0F2FE", fg: "#075985", label: "Enforce" }
    : m === "shadow"
    ? { bg: "#FEF3C7", fg: "#7c5b10", label: "Shadow" }
    : { bg: colors.neutral[100], fg: colors.neutral[700], label: m || "—" };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: 1.25, height: 22, borderRadius: 999,
      backgroundColor: cfg.bg, color: cfg.fg,
      fontSize: 11.5, fontWeight: 600,
    }}>{cfg.label}</Box>
  );
}

function SkeletonContent(props) {
  const { rowsPerPage } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = [0, 1, 2, 3, 4, 5, 6, 7];

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "8px",
                }}
              />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
};
const RateLimitEventPage = () => {
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWafEventController,
  } = useAuth();
  const { PERMISSIONS } = usePermissions();
  const {
    rlEvents,
    rlEvent,
    time_range,
    total_rl_events_count,
    // from,
    // rows_per_page,
    getOnlyRlEvents,
    getRlEvents,
    getRlEvent,
    getRlEventStats,
    rlEventLoading,
  } = useEvent();
  const { getTopRlEventsInfo } = useRateLimit();
  const { curProjectId } = useHome();
  const { apiSiteId: siteID } = useGlobalSite();

  // Check if user has permission to view rate limit events

  const [timeRange, setTimeRange] = useState(time_range.period);
  const [customRangeConfirm, setCustomRangeConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(() => getDefaultEventListingCustomDateRange());
  const [activeFilters, setActiveFilters] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isFilteringById, setIsFilteringById] = useState(false);
  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  const getTimeRange = () => {
    if (timeRange === "custom") {
      const now = new Date();
      const timeZone =
        now.getTimezoneOffset() >= 0
          ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
          : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    } else {
      return { period: timeRange };
    }
  };

  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const apiConditions = useMemo(
    () => withSearchCondition(
      buildConditionsFromFilters(
        activeFilters.filter((f) => f.id !== "id"),
        "ratelimit"
      ),
      query
    ),
    [activeFilters, query]
  );

  const getEventsWithFillter = async () => {
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    
    // Check if there's an ID filter - use dedicated GET endpoint
    const idFilter = activeFilters.find((f) => f.id === "id" && f.value);
    if (idFilter) {
      setIsFilteringById(true);
      setAllEvents([]);
      setHasMore(false);
      isInitialLoadRef.current = false;
      await getRlEvent(idFilter.value);
      return;
    }
    
    setIsFilteringById(false);
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    getTopRlEventsInfo(setWafEventController, siteID, getTimeRange());
    getRlEvents(setWafEventController, siteID, getTimeRange(), ITEMS_PER_PAGE, 0, WafAction.ALL, apiConditions);
    getRlEventStats(setWafEventController, siteID, getTimeRange(), apiConditions);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    getEventsWithFillter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, siteID, customDateRange, timeRange, curProjectId, activeFilters, query]);

  const [allEvents, setAllEvents] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const ITEMS_PER_PAGE = 10;

  React.useEffect(() => {
    if (rlEvents !== null && !isFilteringById) {
      if (rlEvents.length === 0 && isInitialLoadRef.current) {
        setAllEvents([]); 
        setHasMore(false);
      } else if (rlEvents.length > 0) {
        if (isInitialLoadRef.current) {
          const initialEvents = rlEvents.slice(0, ITEMS_PER_PAGE);
          setAllEvents(initialEvents);
          setHasMore(initialEvents.length === ITEMS_PER_PAGE && rlEvents.length >= ITEMS_PER_PAGE && initialEvents.length < total_rl_events_count);
          isInitialLoadRef.current = false;
        } else {
          setAllEvents((prev) => {
            const newEventsToAdd = rlEvents.slice(0, ITEMS_PER_PAGE);
            const newEvents = [...prev, ...newEventsToAdd];
            setHasMore(newEvents.length < total_rl_events_count);
            return newEvents;
          });
        }
      }
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rlEvents, total_rl_events_count, isFilteringById]);

  // Handle single event fetch (when filtering by ID)
  React.useEffect(() => {
    if (isFilteringById) {
      if (rlEvent) {
        // Normalize single event structure to match list format
        const normalizedEvent = { ...rlEvent };
        if (normalizedEvent.type && typeof normalizedEvent.type === "object" && !Array.isArray(normalizedEvent.type)) {
          normalizedEvent.type = [normalizedEvent.type];
        }
        setAllEvents([normalizedEvent]);
      } else {
        setAllEvents([]);
      }
    }
  }, [rlEvent, isFilteringById]);

  // Update selectedEvent with full event data when rlEvent is fetched
  React.useEffect(() => {
    if (rlEvent && selectedEvent && rlEvent.id === selectedEvent.id && drawerOpen) {
      setSelectedEvent(rlEvent);
    }
  }, [rlEvent, selectedEvent, drawerOpen]);

  React.useEffect(() => {
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID, customDateRange, timeRange, curProjectId, activeFilters, query]);

  React.useEffect(() => {
    if (!hasMore || isLoadingMore || allEvents.length < ITEMS_PER_PAGE) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = document.querySelector('.ps');
    const root = perfectScrollbar || null;
    const rootMargin = '200px';

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allEvents.length >= ITEMS_PER_PAGE && allEvents.length < total_rl_events_count) {
            setIsLoadingMore(true);
            const nextFrom = allEvents.length;
            const currentTimeRange = getTimeRange();
            getOnlyRlEvents(siteID, currentTimeRange, ITEMS_PER_PAGE, nextFrom, WafAction.ALL, apiConditions);
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, allEvents.length, siteID, timeRange, customDateRange, apiConditions, getOnlyRlEvents, total_rl_events_count]);

  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setDrawerOpen(true);
    if (event.id) {
      await getRlEvent(event.id);
    }
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  const upsertSimilarFilter = (filters, { id, label, value }) => {
    if (!id || !value) return filters;
    const existingIndex = filters.findIndex(
      (f) => f.id === id || (id === "status" && f.id === "action")
    );
    if (existingIndex >= 0) {
      const next = [...filters];
      next[existingIndex] = { id, label, value };
      return next;
    }
    return [...filters, { id, label, value }];
  };

  const handleFilterSimilar = (filterType, event) => {
    if (!event) return;

    if (filterType === "burst") {
      const burstFilters = [
        { id: "src_ip", label: "IP Address", value: event.src_ip || event.ip },
        { id: "hostname", label: "Hostname", value: event.host_name || event.hostname || event.host },
        { id: "uri", label: "URI Path", value: event.uri || event.path || event.request_uri },
      ].filter((f) => f.value);

      if (burstFilters.length === 0) return;

      let nextFilters = [...activeFilters];
      burstFilters.forEach((bf) => {
        nextFilters = upsertSimilarFilter(nextFilters, bf);
      });
      handleFiltersChange(nextFilters);
      return;
    }

    let filterId = null;
    let filterValue = null;
    let filterLabel = null;

    switch (filterType) {
      case "ip":
        filterId = "src_ip";
        filterValue = event.src_ip || event.ip;
        filterLabel = "IP Address";
        break;
      case "hostname":
        filterId = "hostname";
        filterValue = event.host_name || event.hostname || event.host;
        filterLabel = "Hostname";
        break;
      case "uri":
        filterId = "uri";
        filterValue = event.uri || event.path || event.request_uri;
        filterLabel = "URI Path";
        break;
      case "country":
        filterId = "country";
        filterValue = event.country_iso_code || event.country_name;
        filterLabel = "Country";
        break;
      case "status":
        filterId = "status";
        filterValue = getRateLimitActionFilterLabel(event);
        filterLabel = "Status";
        break;
      default:
        return;
    }

    if (!filterId || !filterValue) return;

    handleFiltersChange(
      upsertSimilarFilter(activeFilters, { id: filterId, label: filterLabel, value: filterValue })
    );
  };
  // Show permission denied message if user doesn't have rate limit read permission
  return (
    <PermissionGate permission={PERMISSIONS.RATE_LIMIT_READ} resourceLabel="Rate Limit events">
    <>
      <Helmet title="Rate Limit Event" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader title="Rate Limit Event" />
        <SiteScopeGate mode="any">
          {() => (
            <>
        <EventFilterBar
          eventType="ratelimit"
          activeFilters={activeFilters}
          onFiltersChange={handleFiltersChange}
          events={allEvents}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search host, URI, IP, UA…"
          searchPending={search.trim() !== query}
          timeRangeComponent={
            <EventsTimeRangePicker
              selectPeriod={selectPeriod}
              selectCustomDateRange={selectCustomDateRange}
              timeRange={timeRange}
              customDateRange={customDateRange}
            />
          }
          onRefresh={getEventsWithFillter}
          refreshing={rlEventLoading}
        />
        <Stack>
          <Box ref={tableContainerRef} spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography sx={tableHeadSx}>Event ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Activity</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Source IP</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Rule</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Mode</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 230 }}>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 48 }}>
                    {/* Eye view column — not labeled "Action" (that name is used by the rule Action data column). */}
                    <Typography sx={tableHeadSx} />
                  </TableCell>
                </TableRow>
              </TableHead>
              {rlEvents === null && allEvents.length === 0 ? (
                <SkeletonContent rowsPerPage={ITEMS_PER_PAGE} />
              ) : allEvents.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography textAlign="center" sx={tableBodySx}>
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {allEvents?.map((item, index) => (
                    <TableRow key={`detail_data_${item?.id || index}`} onClick={() => handleEventClick(item)} sx={{ cursor: "pointer" }}  >
                      <TableCell>
                        <Typography
                          sx={{
                            ...tableBodySx,
                            fontFamily: "monospace",
                            color: "#1E40AF",
                            wordBreak: "break-all",
                          }}
                        >
                          {item?.id || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{item?.host_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Typography sx={tableBodySx}>
                            {item?.resStatus} {item?.method}
                          </Typography>
                          {item?.uri?.length > 36 ? (
                            <Tooltip title={item?.uri} arrow>
                              <Typography sx={{ ...tableBodySx, color: colors.primary[300] }}>{item?.uri?.substr(0, 36) + " ..."}</Typography>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ ...tableBodySx, color: colors.primary[300] }}>{item?.uri}</Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems={"center"}>
                          <ReactCountryFlag
                            countryCode={item?.country_iso_code}
                            svg
                            title={item?.country_name}
                            style={{
                              width: "26px",
                              height: "18px",
                            }}
                          />
                          <Typography sx={{ ...tableBodySx, pl: 2 }}>{item?.src_ip}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ ...tableBodySx, fontWeight: 500 }}>
                            {item?.rule_name ?? "—"}
                          </Typography>
                          {(item?.limit != null && item?.period_s != null) && (
                            <Typography sx={{ ...tableBodySx, fontSize: 12, color: colors.neutral[600] }}>
                              {item.limit} / {item.period_s}s
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <RlActionPill action={item?.action} blocked={item?.blocked_bool || item?.sd_rate_limited === 1} />
                      </TableCell>
                      <TableCell>
                        <RlModePill mode={item?.mode} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{formatDate(item?.timestamp) || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        {/* <Stack
                          alignItems={"flex-end"}
                          onClick={() =>
                            navigate(
                              `/v2/detection/rate_limit_event/view/${DetectionFilterCategory.TopWAFDetectionsByCountry}/${
                                item?.id
                              }?data=${encodeURIComponent(item?.country_iso_code)}`
                            )
                          }
                          sx={{ cursor: "pointer" }}
                        > */}
                          <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                        {/* </Stack> */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
            {hasMore && allEvents.length >= ITEMS_PER_PAGE && (
              <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />
            )}
            {isLoadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!hasMore && allEvents.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>
                  No more events to load
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <StyledMenu
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <PeriodItemComponent selectPeriod={selectPeriod} />
      </StyledMenu>
      <EventDetailDrawer 
        open={drawerOpen} 
        onClose={handleDrawerClose} 
        event={selectedEvent} 
        event_Type="ratelimit"
        onFilterSimilar={handleFilterSimilar}
      />
    </>
    </PermissionGate>
  );
};
export default RateLimitEventPage;
